import React, { useState, useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import * as DOMPurify from "dompurify";
import { Tab, Tabs } from "@mui/material";

import CreateTrplBtn from "../CreateTrplBtn";
import SetDeleteTrplBtns from "../SetDeleteTrplBtns";
import { shouldShowTrplAsEdited } from "../../../../utils";

import EditorToolbar, { modules, formats } from "./EditorToolbar2";

import { quillCoreStyles } from "./styles";

const preventDeprecatedEvents = () => {
  if (!EventTarget.prototype._patched) { // Avoid repeated patching
    const originalAddEventListener = EventTarget.prototype.addEventListener;
    EventTarget.prototype.addEventListener = function (type, listener, options) {
      if (type === 'DOMNodeInserted') {
        return;
      }
      return originalAddEventListener.call(this, type, listener, options);
    };
    EventTarget.prototype._patched = true;
  }
};

export default ({
  rst,
  trpl,
  type,
  groupId,
  setTrpl,
  classes,
  constant,
  isNewNode,
  isVirtual,
  trplIndex,
  tidsChains,
  ancestorId,
  loadingTids,
  deletedTids,
  hideSetDelete,
  isAnotherNode,
  setLoadingTids,
  onRstValueEdit,
  removeTrplValue,
  setDeletedTrplId,
  resetDeletedTrplId,
  showMandatoryAlert,
  disableTrplEditing,
  updateDataOnRestoreTrpl,
}) => {
  const [editorContent, setEditorContent] = useState(trpl.val || "");
  const [selectedTab, setSelectedTab] = useState("editor");
  const [loaded, setLoaded] = useState(false);
  const quillRef = useRef(null);

  // Inject Quill styles globally
  const injectQuillStyles = () => {
    if (!document.querySelector("#quill-custom-styles")) {
      const style = document.createElement("style");
      style.id = "quill-custom-styles";
      style.type = "text/css";
      style.appendChild(document.createTextNode(quillCoreStyles));
      document.head.appendChild(style);
    }
    setLoaded(true);
  };

  // Inject styles on component mount
  useEffect(() => {
    injectQuillStyles();
    preventDeprecatedEvents();
  }, []);

  const setHtml = (content, delta, source, editor) => {
    if(source === "user") {
      const value = editor.getHTML();
      setEditorContent(value);
      onRstValueEdit(rst, value, trplIndex, ancestorId, tidsChains);
    }
  };

  const handleEditorChange = (content) => {
    setEditorContent(content);
    onRstValueEdit(rst, content, trplIndex, ancestorId, tidsChains);
  };

  const clean = DOMPurify.sanitize(editorContent, { USE_PROFILES: { html: true } });

  const notEditable = constant || disableTrplEditing;

  const textAreaClassName = `${classes.trplTextFieldInput}
    ${showMandatoryAlert && !trpl.tid ? classes.mandatoryField : ""}
    ${shouldShowTrplAsEdited(trpl)
      ? classes.trplEdited
      : isVirtual && notEditable
      ? classes.notEditableVirtualTrpl
      : isVirtual
      ? classes.trplVirtual
      : notEditable && trpl && trpl.tid
      ? classes.notEditableTrplWithValue
      : trpl && trpl.tid
      ? classes.trplWithValue
      : classes.trplEmpty
    }`;

  if (!loaded) return null;

  return (
    <div
      key={`${rst.id}.${trpl.tid}.${trplIndex}`}
      className={classes.alignCenter}
      style={{
        height: "100%",
        width: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          //overflow: "hidden",
          position: "relative",
        }}
      >
        <div style={{ width: "fit-content", padding: "1px 5px 5px" }}>
          <Tabs
            selectionFollowsFocus
            className={`${classes.smallestTabs} ${classes.mr20}`}
            value={selectedTab}
            onChange={(e, value) => setSelectedTab(value)}
          >
            <Tab value={"html"} label={"HTML"} />
            <Tab value={"editor"} label={"Text Editor"} />
            <Tab value={"preview"} label={"Preview"} />
          </Tabs>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            height: "100%",
            width: "100%",
            minHeight: '300px',
            overflowY: "auto",
            position: "relative",
          }}
        >
          {selectedTab === "html" ? (
            <textarea
              placeholder="Paste your HTML code here..."
              value={editorContent}
              onChange={(e) => handleEditorChange(e.target.value)}
              className={`${classes.resizableTextarea} ${textAreaClassName}`}
              style={{
                position: "relative",
                height: "300px",
                width: "100%",
                minHeight: "50px",
                maxHeight: "1000px",
                resize: "vertical",
              }}
            />
          ) : selectedTab === "editor" ? (
            <div
            className={`${classes.resizableTextarea} ${textAreaClassName}`}
              style={{
                flex: 1,
                display: "flex",
                height: 'fit-content',
                flexDirection: "column",
                overflowY: "auto",
                minHeight: '300px',
              }}
            >
              <EditorToolbar />
              <ReactQuill
                theme="snow"
                ref={quillRef}
                value={editorContent}
                onChange={setHtml}
                modules={modules}
                formats={formats}
                placeholder="Write something..."
                className="quill"  // Add className to ensure styles are applied
              />
            </div>
          ) : (
            <div
              className={`${classes.fillAvailable}`}
              style={{
                border: "none",
                overflow: "auto",
                margin: "5px",
                padding: "5px",
              }}
              dangerouslySetInnerHTML={{ __html: clean }}
            />
          )}
        </div>
      </div>

      {!hideSetDelete &&
        (trpl && trpl.tid ? (
          <SetDeleteTrplBtns
            rst={rst}
            type={type}
            trpl={trpl}
            classes={classes}
            groupId={groupId}
            setTrpl={setTrpl}
            trplIndex={trplIndex}
            constant={constant}
            ancestorId={ancestorId}
            loadingTids={loadingTids}
            isAnotherNode={isAnotherNode}
            setLoadingTids={setLoadingTids}
            removeTrplValue={removeTrplValue}
            setDeletedTrplId={setDeletedTrplId}
          />
        ) : (
          <CreateTrplBtn
            rst={rst}
            trpl={trpl}
            trplId={rst.id}
            classes={classes}
            setTrpl={setTrpl}
            isNewNode={isNewNode}
            trplIndex={trplIndex}
            constant={constant}
            ancestorId={ancestorId}
            loadingTids={loadingTids}
            deletedTids={deletedTids}
            isAnotherNode={isAnotherNode}
            setLoadingTids={setLoadingTids}
            resetDeletedTrplId={resetDeletedTrplId}
            disableTrplEditing={disableTrplEditing}
            updateDataOnRestoreTrpl={updateDataOnRestoreTrpl}
          />
        ))}
    </div>
  );
};
